import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { validatePositiveNumber } from '../../utils/validators';
import { EXPENSE_STATUS, FILE_UPLOAD } from '../../utils/constants';
import { formatFileSize } from '../../utils/formatters';
import { useCategories } from '../../hooks/useCategories';
import Button from '../common/Button';
import { Upload, X, FileText } from 'lucide-react';

const ExpenseForm = ({ initialData, onSubmit, isLoading }) => {
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(initialData?.receiptPath || null);
  const [fileError, setFileError] = useState('');

  const { data: categories, isLoading: categoriesLoading } = useCategories();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialData || {
      description: '',
      amount: '',
      expenseDate: '',
      categoryId: '',
      notes: '',
      status: 'pending',
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');

    if (!file) {
      setReceiptFile(null);
      setReceiptPreview(null);
      return;
    }

    // Validate file type
    if (!FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      setFileError('Tipo de arquivo não permitido. Use JPG, PNG ou PDF.');
      return;
    }

    // Validate file size
    if (file.size > FILE_UPLOAD.MAX_SIZE) {
      setFileError(`Arquivo muito grande. Máximo: ${formatFileSize(FILE_UPLOAD.MAX_SIZE)}`);
      return;
    }

    setReceiptFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
  };

  const removeFile = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    setFileError('');
  };

  const handleFormSubmit = async (data) => {
    const formData = {
      ...data,
      amount: parseFloat(data.amount),
      receiptFile: receiptFile || null,
    };

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Description */}
        <div className="md:col-span-2">
          <label htmlFor="description" className="label">
            Descrição *
          </label>
          <input
            id="description"
            type="text"
            className={`input ${errors.description ? 'input-error' : ''}`}
            {...register('description', {
              required: 'Descrição é obrigatória',
              minLength: {
                value: 3,
                message: 'Descrição deve ter no mínimo 3 caracteres',
              },
            })}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Amount */}
        <div>
          <label htmlFor="amount" className="label">
            Valor (R$) *
          </label>
          <input
            id="amount"
            type="number"
            step="0.01"
            min="0"
            className={`input ${errors.amount ? 'input-error' : ''}`}
            {...register('amount', {
              required: 'Valor é obrigatório',
              validate: (value) =>
                validatePositiveNumber(value) || 'Valor deve ser maior que zero',
            })}
          />
          {errors.amount && (
            <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
          )}
        </div>

        {/* Expense Date */}
        <div>
          <label htmlFor="expenseDate" className="label">
            Data da Despesa *
          </label>
          <input
            id="expenseDate"
            type="date"
            className={`input ${errors.expenseDate ? 'input-error' : ''}`}
            {...register('expenseDate', {
              required: 'Data é obrigatória',
            })}
          />
          {errors.expenseDate && (
            <p className="mt-1 text-sm text-red-600">{errors.expenseDate.message}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label htmlFor="categoryId" className="label">
            Categoria *
          </label>
          <select
            id="categoryId"
            className={`input ${errors.categoryId ? 'input-error' : ''}`}
            {...register('categoryId', { required: 'Categoria é obrigatória' })}
          >
            <option value="">Selecione uma categoria</option>
            {categoriesLoading ? (
              <option disabled>Carregando...</option>
            ) : (
              categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))
            )}
          </select>
          {errors.categoryId && (
            <p className="mt-1 text-sm text-red-600">{errors.categoryId.message}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="label">
            Status *
          </label>
          <select
            id="status"
            className={`input ${errors.status ? 'input-error' : ''}`}
            {...register('status', { required: 'Status é obrigatório' })}
          >
            <option value={EXPENSE_STATUS.PENDING}>Pendente</option>
            <option value={EXPENSE_STATUS.APPROVED}>Aprovado</option>
            <option value={EXPENSE_STATUS.REJECTED}>Rejeitado</option>
          </select>
          {errors.status && (
            <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
          )}
        </div>

        {/* Notes */}
        <div className="md:col-span-2">
          <label htmlFor="notes" className="label">
            Observações
          </label>
          <textarea
            id="notes"
            rows="3"
            className={`input ${errors.notes ? 'input-error' : ''}`}
            {...register('notes')}
          />
          {errors.notes && (
            <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
          )}
        </div>

        {/* File Upload */}
        <div className="md:col-span-2">
          <label className="label">Comprovante</label>

          {!receiptFile && !receiptPreview ? (
            <div className="mt-1">
              <label
                htmlFor="receipt"
                className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-primary-400 transition-colors"
              >
                <div className="space-y-2 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-primary-600 hover:text-primary-500">
                      Clique para fazer upload
                    </span>
                    {' '}ou arraste o arquivo
                  </div>
                  <p className="text-xs text-gray-500">
                    PNG, JPG ou PDF até {formatFileSize(FILE_UPLOAD.MAX_SIZE)}
                  </p>
                </div>
                <input
                  id="receipt"
                  type="file"
                  className="sr-only"
                  accept={FILE_UPLOAD.ALLOWED_TYPES.join(',')}
                  onChange={handleFileChange}
                />
              </label>
            </div>
          ) : (
            <div className="mt-1 border-2 border-gray-300 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {receiptPreview && receiptFile?.type.startsWith('image/') ? (
                    <img
                      src={receiptPreview}
                      alt="Preview"
                      className="h-16 w-16 object-cover rounded"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-gray-100 rounded flex items-center justify-center">
                      <FileText className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {receiptFile?.name || 'Comprovante anexado'}
                    </p>
                    {receiptFile && (
                      <p className="text-xs text-gray-500">
                        {formatFileSize(receiptFile.size)}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {fileError && (
            <p className="mt-1 text-sm text-red-600">{fileError}</p>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="secondary"
          onClick={() => window.history.back()}
        >
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={isLoading}>
          {initialData ? 'Atualizar' : 'Cadastrar'}
        </Button>
      </div>
    </form>
  );
};

export default ExpenseForm;
